import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "./firebase";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signOut,
  deleteUser,
  updateProfile,
} from "firebase/auth";
import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import logo from "./assets/wgwitlogov1white-02.png";

function Signup() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "user",
    acceptTerms: false, // NEW field
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;

    // Sanitize and limit username in real time:
    if (name === "username") {
      // Keep only letters, numbers, and underscores; cap at 15 chars
      const cleaned = value.replace(/[^A-Za-z0-9_]/g, "").slice(0, 15);
      setFormData((prev) => ({
        ...prev,
        [name]: cleaned,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const {
      firstName,
      lastName,
      username,
      email,
      password,
      confirmPassword,
      role,
      acceptTerms,
    } = formData;

    // Re-clean on submit in case someone bypassed the UI
    const rawUname = username.trim();
    const cleanedSubmit = rawUname.replace(/[^A-Za-z0-9_]/g, "");

    // Reject if any illegal characters were present
    if (cleanedSubmit !== rawUname) {
      setError("Username can only contain letters, numbers, and underscores.");
      return;
    }

    // Enforce length bounds strictly
    if (cleanedSubmit.length < 6) {
      setError("Username must be at least 6 characters.");
      return;
    }
    if (cleanedSubmit.length > 15) {
      setError("Username cannot exceed 15 characters.");
      return;
    }

    // Final regex guard (defense in depth)
    if (!/^[A-Za-z0-9_]{6,15}$/.test(cleanedSubmit)) {
      setError(
        "Username must be 6-15 characters and use only letters, numbers, and underscores."
      );
      return;
    }

    // Store lowercase version
    const uname = cleanedSubmit.toLowerCase();

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (!acceptTerms) {
      setError("You must accept the Terms of Service to sign up.");
      return;
    }

    setLoading(true);
    let createdUser = null;

    try {
      // 1) Create Auth user (this signs them in)
      const cred = await createUserWithEmailAndPassword(
        auth,
        email.trim(),
        password
      );
      createdUser = cred.user;

      // Optional: set displayName in Auth profile
      const displayName = `${firstName.trim()} ${lastName.trim()}`.trim();
      if (displayName) {
        await updateProfile(createdUser, { displayName });
      }

      const uid = createdUser.uid;
      const emailLower = (createdUser.email || "").toLowerCase();

      // 2) Atomically reserve username and create users/{uid} exactly once
      await runTransaction(db, async (tx) => {
        const unameRef = doc(db, "usernames", uname);
        const userRef = doc(db, "users", uid);

        const unameSnap = await tx.get(unameRef);
        if (unameSnap.exists()) {
          throw new Error("Username is already taken.");
        }

        const userSnap = await tx.get(userRef);
        if (userSnap.exists()) {
          throw new Error("User profile already exists.");
        }

        // Create users/{uid}
        tx.set(userRef, {
          uid,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          username: uname, // stored lowercased
          email: createdUser.email,
          role: role || "user",
          credits: 0,
          verified: false,
          createdAt: serverTimestamp(),
        });

        // Reserve usernames/{username}
        // Include emailLower so username login can resolve without user_public
        tx.set(unameRef, {
          uid,
          username: uname, // keep lowercased to match doc id
          emailLower,      // optional but recommended by rules
        });
      });

      // 3) Ensure public mirror for username login fallback
      // Owner is signed in, rules allow this create/update
      await setDoc(
        doc(db, "user_public", createdUser.uid),
        { emailLower },
        { merge: true }
      );

      // 4) Send verification email only after Firestore succeeds
      await sendEmailVerification(createdUser, {
        url: "https://whatgradewillitget.com/verify-confirm",
      });

      // 5) Sign out and send them to verify page
      await signOut(auth);
      navigate("/verify-email");
    } catch (err) {
      console.error(err);
      if (
        createdUser &&
        auth.currentUser &&
        createdUser.uid === auth.currentUser.uid
      ) {
        try {
          await deleteUser(auth.currentUser);
        } catch (delErr) {
          console.warn("Failed to rollback Auth user:", delErr);
        }
      }

      if (err.code === "auth/email-already-in-use") {
        setError(
          "An account with this email already exists. Try logging in instead."
        );
      } else if (err.message === "Username is already taken.") {
        setError("Username is already taken.");
      } else if (err.message === "User profile already exists.") {
        setError("A profile already exists for this user.");
      } else {
        setError(
          err?.message ||
            "Sign up failed. Please check your details and try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.logoContainer}>
        <img src={logo} alt="Logo" style={styles.logo} />
        <span style={styles.siteName}>pregradepros.com</span>
      </div>

      <button onClick={() => navigate("/")} style={styles.backButton}>
        ← Back to Home
      </button>

      <div style={styles.container}>
        <h2 style={styles.heading}>Sign Up</h2>
        <form onSubmit={handleSubmit} style={styles.form}>
          <input
            type="text"
            name="firstName"
            placeholder="First Name"
            onChange={handleChange}
            required
            style={styles.input}
            autoComplete="given-name"
          />
          <input
            type="text"
            name="lastName"
            placeholder="Last Name"
            onChange={handleChange}
            required
            style={styles.input}
            autoComplete="family-name"
          />
          <input
            type="text"
            name="username"
            placeholder="Username (6–15; letters, numbers, _)"
            onChange={handleChange}
            value={formData.username}
            required
            maxLength={15}
            pattern="^[A-Za-z0-9_]{6,15}$"
            title="Use 6–15 characters: letters, numbers, and underscores only."
            style={styles.input}
            autoComplete="username"
            inputMode="text"
          />
          <input
            type="email"
            name="email"
            placeholder="Email (for records)"
            onChange={handleChange}
            required
            style={styles.input}
            autoComplete="email"
          />
          <input
            type="password"
            name="password"
            placeholder="Password"
            onChange={handleChange}
            required
            style={styles.input}
            autoComplete="new-password"
          />
          <input
            type="password"
            name="confirmPassword"
            placeholder="Confirm Password"
            onChange={handleChange}
            required
            style={styles.input}
            autoComplete="new-password"
          />

          <label style={styles.checkboxContainer}>
            <input
              type="checkbox"
              name="acceptTerms"
              checked={formData.acceptTerms}
              onChange={handleChange}
              required
              style={styles.checkbox}
            />
            <span>
              I accept the{" "}
              <a
                href="https://whatgradewillitget.com/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
                style={styles.link}
              >
                Terms of Service
              </a>
            </span>
          </label>

          {error && <p style={styles.error}>{error}</p>}
          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? "Creating account..." : "Sign Up"}
          </button>
        </form>

        <p style={styles.text}>
          Already have an account?{" "}
          <a href="/login" style={styles.link}>
            Login
          </a>
        </p>

        <div style={styles.spacer} />
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    minHeight: "100vh",
    backgroundColor: "#000",
    color: "#fff",
    fontFamily: "'Montserrat', sans-serif",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingTop: "20px",
    paddingLeft: "20px",
    paddingRight: "20px",
  },
  logoContainer: {
    position: "absolute",
    top: "20px",
    left: "20px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  logo: {
    height: "40px",
  },
  siteName: {
    fontSize: "20px",
    fontWeight: "bold",
  },
  backButton: {
    marginTop: "60px",
    marginBottom: "25px",
    padding: "10px 20px",
    backgroundColor: "#1976d2",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "bold",
  },
  container: {
    maxWidth: "460px",
    width: "100%",
    textAlign: "center",
  },
  heading: {
    fontSize: "27.6px",
    fontWeight: 700,
    marginBottom: "23px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    textAlign: "left",
  },
  input: {
    padding: "13.8px",
    fontSize: "18.4px",
    borderRadius: "6.9px",
    border: "1px solid #555",
    backgroundColor: "#111",
    color: "#fff",
  },
  checkboxContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "15px",
  },
  checkbox: {
    transform: "scale(1.2)",
    marginRight: "6px",
  },
  button: {
    padding: "13.8px",
    backgroundColor: "#1976d2",
    color: "#fff",
    border: "none",
    borderRadius: "6.9px",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "18.4px",
  },
  error: {
    color: "red",
    fontSize: "16.1px",
    marginTop: "-10px",
  },
  text: {
    marginTop: "23px",
    fontSize: "16.1px",
  },
  link: {
    color: "#4dabf7",
    textDecoration: "underline",
  },
  spacer: {
    width: "100%",
    maxWidth: "100%",
    height: "60px",
  },
};

export default Signup;